import {
  FileText,
  Files,
  ChevronDown,
  PanelRightClose,
  PanelRightOpen,
} from 'lucide-react';
import { useDetectedMetadata } from './DetectedMetadata';
import { useEffect, useRef, useState } from 'react';
import { documentDeadline } from '../../utils/documentDeadline';
import DocumentReading from './DocumentReading';
import {
  allowedActions,
  detailStatus,
  DocumentDetail,
  fileLabel,
  filePeriodLabel,
  optionalApproval,
  periodLabel,
  ReviewRequirement,
  timestamp,
  versions,
} from './detailModel';
const secondary =
  'rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50';
export default function ReviewWorkspace({
  requirements,
  loading,
  error,
  retry,
  month,
  requestedDocumentId,
  requestedRequirementId,
  role,
  busy,
  onSelect,
  onApprove,
  onReject,
  onRevision,
  onDownload,
  onDelete,
  onDiscard,
  onOptionalApproval,
  onOverride,
  periodicity,
}: {
  requirements: ReviewRequirement[];
  loading: boolean;
  error: string | null;
  retry: () => void;
  month: string;
  requestedDocumentId: string | null;
  requestedRequirementId: string | null;
  role?: string;
  busy: boolean;
  onSelect: (documentId: string | null, requirementId: string) => void;
  onApprove: (doc: DocumentDetail) => void;
  onReject: (doc: DocumentDetail, aiSummary?: string) => void;
  onRevision: (doc: DocumentDetail) => void;
  onDownload: (doc: DocumentDetail) => void;
  onDelete: (doc: DocumentDetail) => void;
  onDiscard: (id: string) => void;
  onOptionalApproval: (req: ReviewRequirement) => void;
  onOverride: (definition: ReviewRequirement['definition']) => void;
  periodicity: (req: ReviewRequirement) => string;
}) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigation = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = navigation.current;
    if (!sidebarOpen || !dialog) return;
    const previous = document.activeElement as HTMLElement | null;
    dialog.showModal();
    return () => {
      dialog.close();
      previous?.focus();
    };
  }, [sidebarOpen]);
  useEffect(() => {
    setSidebarOpen(false);
  }, [loading, month, requestedDocumentId, requestedRequirementId]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [pendingOnly, setPendingOnly] = useState(false);
  const [showMetadata, setShowMetadata] = useState(true);
  const pendingFiles = (req: ReviewRequirement) =>
    versions(req).filter(
      (file) => allowedActions(file, role, versions(req)).approve,
    );
  const pendingCount = requirements.reduce(
    (count, req) => count + pendingFiles(req).length,
    0,
  );
  const ordered = [...requirements].sort(
    (a, b) =>
      (a.definition.category?.name || '').localeCompare(
        b.definition.category?.name || '',
        'es',
      ) || a.definition.name.localeCompare(b.definition.name, 'es'),
  );
  const selected = requestedDocumentId
    ? ordered.find((req) =>
        versions(req).some((doc) => doc.id === requestedDocumentId),
      )
    : requestedRequirementId
      ? ordered.find((req) => req.id === requestedRequirementId)
      : ordered[0];
  const files = selected ? versions(selected) : [];
  const doc = requestedDocumentId
    ? files.find((file) => file.id === requestedDocumentId)
    : files[0];
  const permissions = doc ? allowedActions(doc, role, files) : null;
  const privileged = role === 'admin' || role === 'reviewer';
  const metadata = useDetectedMetadata(
    privileged && !loading ? doc?.id || '' : '',
    doc?.file_location || '',
    `${doc?.applicable_year || ''}|${doc?.applicable_month || ''}|${doc?.documentName || ''}`,
  );
  const visibleRequirements = ordered.filter(
    (req) =>
      (!category ||
        (req.definition.category?.name || 'Sin categoría') === category) &&
      (!pendingOnly || pendingFiles(req).length > 0) &&
      req.definition.name
        .toLocaleLowerCase()
        .includes(search.toLocaleLowerCase()),
  );
  const pendingSequence = visibleRequirements.flatMap((req) =>
    pendingFiles(req).map((file) => ({ req, file })),
  );
  const nextPending =
    pendingSequence[
      pendingSequence.findIndex((item) => item.file.id === doc?.id) + 1
    ];

  const fileContent =
    doc && selected ? (
      <div className="text-sm text-slate-600">
        <h4 className="mb-4 font-semibold text-slate-800">
          Archivo y versiones
        </h4>
        <div className="pb-3">
          <p className="break-all font-medium text-slate-800">
            {fileLabel(doc)}
          </p>
          <p className="mt-2 text-xs text-slate-500">
            {periodicity(selected)} · Fecha límite:{' '}
            {documentDeadline(
              {
                expiryDate: doc?.expiry_date,
                dueDate: doc?.requirement.due_date || selected.due_date,
                applicableMonth: doc?.applicable_month,
                applicableYear: doc?.applicable_year,
                renewalType: selected.definition.renewal_type,
                renewalInterval: selected.definition.renewal_interval,
              },
              1,
            )
              ?.split('-')
              .reverse()
              .join('/') || 'Sin fecha límite'}
          </p>
          <p className="mt-1 text-xs">Cargado: {timestamp(doc.upload_date)}</p>
          {doc.approval_timestamp && (
            <p className="mt-1 text-xs">
              Revisado: {timestamp(doc.approval_timestamp)}
            </p>
          )}
          {doc.udr_is_current === false && (
            <p className="mt-1 text-xs">Archivo histórico</p>
          )}
          <button
            disabled={busy}
            onClick={() => onDownload(doc)}
            className="mt-2 rounded-md py-2 text-sm text-blue-700 underline underline-offset-4 disabled:opacity-50"
          >
            Descargar archivo
          </button>
          {files.length > 1 && (
            <div className="mt-4 border-t border-slate-100 pt-3">
              <h3 className="text-sm font-medium">
                Historial de archivos ({files.length})
              </h3>
              <div
                aria-label="Historial de archivos"
                className="mt-3 max-h-64 space-y-1 overflow-y-auto"
              >
                {files.map((file, index) => (
                  <button
                    key={file.id}
                    disabled={busy}
                    aria-current={doc?.id === file.id ? 'true' : undefined}
                    onClick={() => onSelect(file.id, selected.id)}
                    className={`block w-full rounded-lg p-3 text-left text-sm disabled:opacity-50 ${doc?.id === file.id ? 'bg-blue-50 ring-1 ring-inset ring-blue-200' : 'hover:bg-slate-50'}`}
                  >
                    <span className="flex flex-wrap justify-between gap-2 font-medium">
                      <span>{timestamp(file.upload_date)}</span>
                      <span>{detailStatus(file)}</span>
                    </span>
                    <span className="mt-1 block break-all text-xs text-slate-500">
                      {fileLabel(file)}
                      {index === 0 ? ' · Más reciente' : ''}
                      {doc?.id === file.id ? ' · Seleccionado' : ''}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    ) : null;

  const documentNavigation = (
    <div className="review-document-tools">
      <button
        type="button"
        aria-expanded={sidebarOpen}
        aria-controls="period-document-sidebar"
        aria-label={`${sidebarOpen ? 'Ocultar' : 'Mostrar'} documentos (${ordered.length})`}
        onClick={() => setSidebarOpen((v) => !v)}
        className="review-document-picker"
      >
        <Files size={17} aria-hidden="true" />
        <span>Documentos</span>
        <span className="review-document-count">{ordered.length}</span>
        <ChevronDown size={14} aria-hidden="true" />
      </button>
      {privileged && doc && (
        <button
          type="button"
          aria-expanded={showMetadata}
          aria-label="Alternar panel de revisión"
          title={showMetadata ? 'Sólo documento' : 'Mostrar revisión'}
          onClick={() => setShowMetadata((v) => !v)}
          className="review-panel-toggle"
        >
          {showMetadata ? (
            <PanelRightClose size={18} aria-hidden="true" />
          ) : (
            <PanelRightOpen size={18} aria-hidden="true" />
          )}
        </button>
      )}
    </div>
  );
  const documentHeading = selected && (
    <header className="review-document-heading">
      <div className="review-document-identity">
        <span className="review-document-icon" aria-hidden="true">
          <FileText size={24} strokeWidth={1.5} />
        </span>
        <div className="review-document-title">
          <h2>{selected.definition.name}</h2>
          <div className="review-document-subtitle">
            <span>{selected.definition.category?.name || 'Sin categoría'}</span>
            {doc && <span>Periodo del archivo: {filePeriodLabel(doc)}</span>}
          </div>
        </div>
      </div>
      <div className="review-document-navigation">
        {documentNavigation}
        <span className="review-pending-count">{pendingCount} por revisar</span>
      </div>
      {doc && files[0]?.id !== doc.id && (
        <p className="review-historical-notice">
          Estás consultando una versión anterior. Existe un archivo más
          reciente.
        </p>
      )}
    </header>
  );

  if (loading)
    return (
      <div
        role="status"
        className="rounded-xl border border-slate-200 bg-white p-10 text-sm text-slate-600"
      >
        Cargando documentos de {periodLabel(month)}…
      </div>
    );
  if (error)
    return (
      <div
        role="alert"
        className="rounded-xl border border-red-200 bg-white p-6"
      >
        <p className="text-sm text-red-800">{error}</p>
        <button className={`${secondary} mt-3`} onClick={retry}>
          Reintentar documentos del periodo
        </button>
      </div>
    );
  return (
    <section aria-label="Revisión del periodo" className="space-y-3">
      {!selected && documentNavigation}
      <div className="min-w-0">
        <dialog
          ref={navigation}
          id="period-document-sidebar"
          aria-label="Documentos del periodo"
          onCancel={() => setSidebarOpen(false)}
          className="review-document-drawer"
        >
          <button
            type="button"
            onClick={() => setSidebarOpen(false)}
            className="float-right m-3 rounded px-3 py-2 text-sm"
          >
            Cerrar
          </button>
          <div className="border-b border-slate-200 p-4">
            <h2 className="text-sm font-semibold">
              Documentos del periodo{' '}
              <span className="font-normal text-slate-500">
                ({ordered.length})
              </span>
            </h2>
            <label className="mt-3 block text-xs text-slate-600">
              Filtrar documentos
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-sm"
              />
            </label>
            <label className="mt-3 block text-xs text-slate-600">
              Categoría
              <select
                value={category}
                onChange={(event) => setCategory(event.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-sm"
              >
                <option value="">Todas las categorías</option>
                {Array.from(
                  new Set(
                    ordered.map(
                      (req) => req.definition.category?.name || 'Sin categoría',
                    ),
                  ),
                ).map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
            {privileged && (
              <label className="mt-3 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={pendingOnly}
                  onChange={(event) => setPendingOnly(event.target.checked)}
                />
                Por revisar ({pendingCount})
              </label>
            )}
          </div>
          <ul className="max-h-64 overflow-y-auto divide-y divide-slate-100 lg:max-h-[70vh]">
            {visibleRequirements.map((req) => {
              const newest = versions(req)[0];
              return (
                <li key={req.id}>
                  <button
                    disabled={busy}
                    aria-current={selected?.id === req.id ? 'true' : undefined}
                    onClick={() => {
                      setSidebarOpen(false);
                      onSelect(
                        (pendingOnly ? pendingFiles(req)[0]?.id : newest?.id) ||
                          null,
                        req.id,
                      );
                    }}
                    aria-label={`${req.definition.category?.name || 'Sin categoría'} ${req.definition.name} ${newest ? `Última versión: ${detailStatus(newest)} · ${versions(req).length} archivo(s)` : 'Pendiente de carga'}`}
                    className={`w-full px-3 py-2 text-left disabled:opacity-50 ${selected?.id === req.id ? 'border-l-4 border-blue-700 bg-blue-50 pl-3' : 'hover:bg-slate-50'}`}
                  >
                    <span className="mt-1 block text-sm font-medium">
                      {req.definition.name}
                    </span>
                    <span className="mt-1 block text-xs text-slate-600">
                      {newest ? detailStatus(newest) : 'Pendiente de carga'}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          {!visibleRequirements.length && (
            <p className="p-4 text-sm text-slate-600">
              {ordered.length
                ? 'No hay documentos que coincidan con estos filtros.'
                : 'No hay requisitos disponibles para este periodo.'}
            </p>
          )}
        </dialog>
        <div className="min-w-0 space-y-4">
          {!selected ? (
            <div
              role="alert"
              className="rounded-xl border border-amber-200 bg-white p-6"
            >
              <h2 className="font-semibold">
                No se encontró el documento solicitado en este periodo
              </h2>
              <p className="mt-2 text-sm text-slate-600">
                Comprueba el periodo o selecciona otro documento de la lista. No
                se ha seleccionado otro archivo automáticamente.
              </p>
            </div>
          ) : (
            <>
              {!doc && documentHeading}
              {doc ? (
                <article
                  key={doc.id}
                  id={`doc-${doc.id}`}
                  className="review-document-surface"
                >
                  {documentHeading}
                  {doc.rejection_reason && (
                    <details className="border-b border-slate-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
                      <summary className="cursor-pointer font-medium">
                        {doc.rejection_reason.startsWith(
                          'SOLICITUD DE REVISIÓN:',
                        )
                          ? 'Cambios solicitados'
                          : 'Motivo registrado'}
                      </summary>
                      <p className="mt-3 max-h-48 overflow-auto whitespace-pre-wrap">
                        {doc.rejection_reason.replace(
                          /^SOLICITUD DE REVISIÓN:\s*/,
                          '',
                        )}
                      </p>
                    </details>
                  )}
                  {!privileged && <div className="p-4">{fileContent}</div>}
                  <DocumentReading
                    key={doc.id}
                    doc={doc}
                    privileged={privileged}
                    showData={showMetadata}
                    metadata={metadata}
                    fileContent={fileContent}
                  />
                  <div className="review-decision-bar">
                    <div>
                      <h3 className="sr-only">Decisión sobre esta versión</h3>
                      <p className="text-sm font-medium text-slate-700">
                        Estado: {detailStatus(doc)}
                      </p>
                      {doc.udr_is_current === false &&
                        doc.status !== 'pending_approval' && (
                          <p className="mt-2 text-sm text-slate-600">
                            Documento histórico: solo consulta.
                          </p>
                        )}
                    </div>
                    <div className="review-decision-actions">
                      {privileged && nextPending && (
                        <>
                          <button
                            disabled={busy}
                            onClick={() => {
                              if (nextPending)
                                onSelect(
                                  nextPending.file.id,
                                  nextPending.req.id,
                                );
                            }}
                            className="rounded-md px-2 py-2 text-xs font-medium text-blue-700 hover:bg-slate-100 disabled:text-slate-400"
                          >
                            Siguiente pendiente
                          </button>
                        </>
                      )}
                      {permissions?.reject && (
                        <button
                          disabled={busy}
                          className="min-h-[44px] flex-1 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 disabled:opacity-50 sm:flex-none"
                          aria-label="Rechazar documento"
                          onClick={() =>
                            onReject(
                              doc,
                              !metadata.loading &&
                                !metadata.error &&
                                !metadata.reviewError &&
                                metadata.review?.assessment?.recommendation ===
                                  'recommend_reject'
                                ? metadata.review.assessment.summary
                                : undefined,
                            )
                          }
                        >
                          Rechazar
                          <span className="hidden sm:inline"> documento</span>
                        </button>
                      )}
                      {permissions?.approve && (
                        <button
                          disabled={busy}
                          className="min-h-[44px] flex-1 rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700 disabled:opacity-50 sm:flex-none"
                          aria-label="Aprobar documento"
                          onClick={() => onApprove(doc)}
                        >
                          Aprobar
                          <span className="hidden sm:inline"> documento</span>
                        </button>
                      )}
                      {permissions?.revision && (
                        <button
                          disabled={busy}
                          className={secondary}
                          onClick={() => onRevision(doc)}
                        >
                          Solicitar revisión
                        </button>
                      )}
                    </div>
                  </div>
                </article>
              ) : (
                <div className="rounded-xl border border-slate-200 bg-white p-8 text-sm text-slate-600">
                  No se ha cargado un archivo para este documento en{' '}
                  {periodLabel(month)}.
                </div>
              )}
              {privileged && (
                <details key={selected.id} className="px-1 py-2">
                  <summary className="cursor-pointer text-sm font-medium text-slate-600">
                    Opciones del requerimiento
                  </summary>
                  {selected.definition.description && (
                    <p className="mt-3 max-w-3xl text-sm text-slate-600">
                      {selected.definition.description}
                    </p>
                  )}
                  <div className="mt-4 flex flex-wrap gap-3">
                    <button
                      disabled={busy}
                      className={secondary}
                      onClick={() => onOverride(selected.definition)}
                    >
                      Configurar periodicidad
                    </button>
                    {optionalApproval(selected, role) && (
                      <button
                        disabled={busy}
                        className={secondary}
                        onClick={() => {
                          if (
                            window.confirm(
                              `¿Aprobar ${selected.definition.name} sin archivo para ${periodLabel(month)}?`,
                            )
                          )
                            onOptionalApproval(selected);
                        }}
                      >
                        Aprobar sin documento
                      </button>
                    )}
                    {doc && permissions?.discard && (
                      <button
                        disabled={busy}
                        className={secondary}
                        onClick={() => onDiscard(doc.id)}
                      >
                        Descartar pendiente duplicado
                      </button>
                    )}
                    {doc && permissions?.delete && (
                      <button
                        disabled={busy}
                        className="rounded-lg border border-red-200 px-3 py-2 text-sm text-red-800"
                        onClick={() => onDelete(doc)}
                      >
                        Eliminar archivo rechazado
                      </button>
                    )}
                  </div>
                </details>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}
